
import React, { useEffect, useState } from "react";
import StepCard from "../StepCard";
import CrealityInstructions from "./CrealityInstructions";
import OrcaInstructions from "./OrcaInstructions";
import AnycubicInstructions from "./AnycubicInstructions";

interface CalibrationInstructionsProps {
  selectedSlicer: "creality" | "orca" | "anycubic";
  selectedMethod?: "classic" | "yolo";
  onNext: () => void;
  onPrevious: () => void;
  currentPass: number;
  newFlowRatio?: string;
  previousFlowRatioOriginal?: string;
}

const CalibrationInstructions = ({
  selectedSlicer,
  selectedMethod,
  onNext,
  onPrevious,
  currentPass,
  newFlowRatio,
  previousFlowRatioOriginal
}: CalibrationInstructionsProps) => {
  const [checkedSteps, setCheckedSteps] = useState<Record<string, boolean>>({});

  const handleStepCheck = (stepId: string, checked: boolean) => {
    setCheckedSteps(prev => ({
      ...prev,
      [stepId]: checked
    }));
  };

  const handleCheckAll = () => {
    if (selectedSlicer === "creality") {
      if (currentPass === 2) {
        setCheckedSteps(prev => ({
          ...prev,
          "creality-step1-pass2": true,
          "creality-step2-pass2": true,
          "creality-step3-pass2": true,
          "creality-step4-pass2": true,
          "creality-step5-pass2": true,
        }));
      } else {
        setCheckedSteps(prev => ({
          ...prev,
          "creality-step1": true,
          "creality-step2": true,
          "creality-step4": true,
        }));
      }
    } else {
      if (currentPass === 2) {
        const prefix = selectedSlicer === "orca" ? "orca" : "anycubic";
        setCheckedSteps(prev => ({
          ...prev,
          [`${prefix}-step1-pass2`]: true,
          [`${prefix}-step2-pass2`]: true,
          [`${prefix}-step3-pass2`]: true,
          [`${prefix}-step4-pass2`]: true,
          [`${prefix}-step5-pass2`]: true,
        }));
      } else {
        const prefix = selectedSlicer === "orca" ? "orca" : "anycubic";
        setCheckedSteps(prev => ({
          ...prev,
          [`${prefix}-step1`]: true,
          [`${prefix}-step2`]: true,
          [`${prefix}-step4`]: true,
        }));
      }
    }
  };

  // Auto-progress when all steps are checked
  useEffect(() => {
    const totalSteps = selectedSlicer === "creality" ? currentPass === 2 ? 5 : 3 : currentPass === 2 ? 5 : 3;
    const checkedCount = Object.values(checkedSteps).filter(Boolean).length;
    if (checkedCount === totalSteps) {
      setTimeout(() => {
        onNext();
      }, 500);
    }
  }, [checkedSteps, selectedSlicer, onNext, currentPass]);

  return (
    <StepCard 
      title="" 
      onPrevious={onPrevious} 
      showPrevious 
      onCheckAll={handleCheckAll}
      showCheckAll
    >
      <div className="space-y-4">
        {selectedSlicer === "creality" ? (
          <CrealityInstructions 
            currentPass={currentPass} 
            newFlowRatio={newFlowRatio} 
            checkedSteps={checkedSteps} 
            onStepCheck={handleStepCheck} 
            onCheckAll={handleCheckAll}
            previousFlowRatioOriginal={previousFlowRatioOriginal}
          />
        ) : selectedSlicer === "orca" ? (
          <>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
              <h4 className="font-semibold text-lg text-gray-900 dark:text-white">
                Orca Slicer Guide
                <span className={`ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                  currentPass === 1 
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" 
                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                }`}>
                  Pass {currentPass}
                </span>
              </h4>
            </div>
            <OrcaInstructions 
              currentPass={currentPass} 
              selectedMethod={selectedMethod} 
              newFlowRatio={newFlowRatio} 
              checkedSteps={checkedSteps} 
              onStepCheck={handleStepCheck}
              previousFlowRatioOriginal={previousFlowRatioOriginal}
            />
          </>
        ) : (
          <>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
              <h4 className="font-semibold text-lg text-gray-900 dark:text-white">
                Anycubic Next Guide
                <span className={`ml-2 px-2 py-1 rounded-md text-xs font-semibold ${
                  currentPass === 1 
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" 
                    : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                }`}>
                  Pass {currentPass}
                </span>
              </h4>
            </div>
            <AnycubicInstructions 
              currentPass={currentPass} 
              selectedMethod={selectedMethod} 
              newFlowRatio={newFlowRatio} 
              checkedSteps={checkedSteps} 
              onStepCheck={handleStepCheck}
              previousFlowRatioOriginal={previousFlowRatioOriginal}
            />
          </>
        )}
      </div>
    </StepCard>
  );
};

export default CalibrationInstructions;
